import React, { useState, useRef } from 'react';
import { X, Upload, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import { fileSize, fmtDt } from '../../utils/helpers';

export function Badge({ className = '', children }) {
  return <span className={`badge border ${className}`}>{children}</span>;
}

export function Modal({ title, onClose, children, size = 'md' }) {
  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" >
      <div className={`card w-full ${widths[size]} max-h-[90vh] flex flex-col shadow-2xl`} >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h2 className="font-semibold text-gray-100">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmModal({ message, onConfirm, onClose, danger = true }) {
  return (
    <Modal title="Подтвердите действие" onClose={onClose} size="sm">
      <div className="flex gap-3 mb-5">
        <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={20} />
        <p className="text-gray-300 text-sm">{message}</p>
      </div>
      <div className="flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose} autoFocus>Отмена</button>
        <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={() => { onConfirm(); onClose(); }}>
          Подтвердить
        </button>
      </div>
    </Modal>
  );
}

export function Spinner({ size = 20 }) {
  return <Loader2 size={size} className="animate-spin text-blue-400" />;
}

export function EmptyState({ text = 'Нет данных' }) {
  return <div className="text-center py-16 text-gray-600 text-sm">{text}</div>;
}

export function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="label">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
      {children}
      {hint && <p className="text-xs text-gray-600 mt-1">{hint}</p>}
    </div>
  );
}

export function Select({ value, onChange, options, placeholder, className = '' }) {
  return (
    <div className="relative">
      <select value={value || ''} onChange={e => onChange(e.target.value)} className={`input appearance-none pr-8 ${className}`}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
    </div>
  );
}

export function FileUploadZone({ onFiles, accept }) {
  const [dragging, setDragging] = useState(false);
  const ref = useRef();
  const handle = (files) => { if (files?.length) onFiles(Array.from(files)); };
  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
        ${dragging ? 'border-blue-500 bg-blue-900/10' : 'border-gray-700 hover:border-gray-600'}`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files); }}
      onClick={() => ref.current?.click()}
    >
      <Upload size={20} className="mx-auto mb-2 text-gray-500" />
      <p className="text-sm text-gray-500">Перетащите файлы или нажмите для выбора</p>
      <p className="text-xs text-gray-600 mt-1">Макс. 15MB</p>
      <input ref={ref} type="file" className="hidden" multiple accept={accept} onChange={e => handle(e.target.files)} />
    </div>
  );
}

export function ProgressBar({ value = 0 }) {
  const pct = Math.min(100, Math.max(0, value));
  const color = pct === 100 ? 'bg-green-500' : pct > 60 ? 'bg-blue-500' : pct > 30 ? 'bg-yellow-500' : 'bg-gray-600';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-800 rounded-full h-2">
        <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs mono text-gray-400 w-8 text-right">{pct}%</span>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-0 border-b border-gray-800">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-300'
          }`}>
          {t.label}
          {t.count != null && <span className="ml-1.5 text-xs bg-gray-800 px-1.5 py-0.5 rounded-full">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function StatusTimeline({ entries }) {
  return (
    <div className="space-y-0">
      {entries.map((e, i) => (
        <div key={e.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1 shrink-0" />
            {i < entries.length - 1 && <div className="w-px flex-1 bg-gray-800 my-1" />}
          </div>
          <div className="pb-4">
            <div className="text-sm font-medium text-gray-200">{e.label}</div>
            <div className="text-xs text-gray-500">{fmtDt(e.changed_at)} · {e.by_name || '—'}</div>
            {e.note && <div className="text-xs text-gray-400 mt-0.5 italic">{e.note}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
